import * as React from 'react';
import { Check, PlusCircle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import i18n from '@/utils/i18n';

interface BaseOption {
  getLabel: () => string;
  value: string;
}

interface DefaultOption extends BaseOption {
  icon?: React.ComponentType<{ className?: string }>;
}

/** Non-selectable items that can appear in the options array. */
type SeparatorEntry = { type: 'separator' };
type TextEntry = { type: 'text'; getLabel: () => string };
type NonSelectableEntry = SeparatorEntry | TextEntry;

/**
 * A single entry in the options list. Can be a selectable option
 * (backward-compatible: `type` defaults to `'option'`), a visual separator,
 * or a group heading text.
 */
export type OptionEntry<TOption extends BaseOption = DefaultOption> =
  | (TOption & { type?: 'option' })
  | NonSelectableEntry;

/** Pre-processed group of options for rendering. */
type OptionGroup<TOption extends BaseOption> = {
  heading?: string;
  showSeparator: boolean;
  items: TOption[];
};

/**
 * Split a mixed options array into groups separated by `separator` / `text`
 * entries. A `text` entry becomes the `heading` of the next group; a
 * `separator` entry inserts a `<CommandSeparator/>` before the next group.
 */
function buildOptionGroups<TOption extends BaseOption>(
  entries: OptionEntry<TOption>[],
): OptionGroup<TOption>[] {
  const groups: OptionGroup<TOption>[] = [];
  let currentHeading: string | undefined;
  let currentItems: TOption[] = [];
  let pendingSeparator = false;

  const flush = () => {
    if (currentItems.length > 0 || currentHeading != null) {
      groups.push({
        heading: currentHeading,
        showSeparator: pendingSeparator,
        items: currentItems,
      });
      currentHeading = undefined;
      currentItems = [];
      pendingSeparator = false;
    }
  };

  for (const entry of entries) {
    if ('type' in entry && entry.type === 'separator') {
      flush();
      pendingSeparator = true;
    } else if ('type' in entry && entry.type === 'text') {
      flush();
      currentHeading = entry.getLabel();
    } else {
      // Selectable option — strip the `type` discriminator for downstream use
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type: _type, ...rest } = entry as TOption & { type?: 'option' };
      currentItems.push(rest as unknown as TOption);
    }
  }
  flush();

  return groups;
}

/** Base props shared between single- and multi-select modes. */
interface DataTableFacetedFilterBaseProps<
  TOption extends BaseOption = DefaultOption,
> {
  title?: string;
  options: OptionEntry<TOption>[];
  triggerClassName?: string | false;
  renderItem?: (option: TOption, isSelected: boolean) => React.ReactNode;
  popoverContentClassName?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  /** Set to `true` when rendered inside a Dialog so the popover scroll works. */
  popoverModal?: boolean;
  /**
   * When provided, the filter operates in server-search mode: client-side
   * filtering is disabled, the search input is debounced upstream, and the
   * callback is invoked with the typed query so the parent can fetch matching
   * `options`. Use together with `loading`.
   */
  onSearch?: (query: string) => void;
  /** Shows a spinner inside the list while async `options` are loading. */
  loading?: boolean;
}

/** Single-select mode (default). Callback emits a string.
 *  `value` also accepts `string[]` for convenience (only the first element is used). */
interface SingleSelectProps<TOption extends BaseOption = DefaultOption>
  extends DataTableFacetedFilterBaseProps<TOption> {
  multiple?: false;
  value?: string | string[];
  onValueChange?: (value: string) => void;
}

/** Multi-select mode. `value` is a string array (or `'__all__'`); callback emits `string | string[]`. */
interface MultiSelectProps<TOption extends BaseOption = DefaultOption>
  extends DataTableFacetedFilterBaseProps<TOption> {
  multiple: true;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
}

type DataTableFacetedFilterProps<TOption extends BaseOption = DefaultOption> =
  | SingleSelectProps<TOption>
  | MultiSelectProps<TOption>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Separator used to encode multiple selected values in a single `ActiveFilter.value` string. */
export const MULTI_VALUE_SEPARATOR = '||';

/** Normalise the external `value` prop into a Set for easy lookup. */
function normaliseSelected(
  value: string | string[] | undefined,
  multiple: boolean,
): Set<string> {
  if (value == null || value === '__all__') return new Set();
  if (Array.isArray(value)) return new Set(value);
  // In multi-select mode a serialised string may contain the separator.
  if (multiple && value.includes(MULTI_VALUE_SEPARATOR)) {
    return new Set(value.split(MULTI_VALUE_SEPARATOR));
  }
  return new Set([value]);
}

/** True when at least one real option is selected. */
function hasSelection(selected: Set<string>): boolean {
  return selected.size > 0;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTableFacetedFilter<
  TOption extends BaseOption = DefaultOption,
>({
  title,
  options,
  value = '__all__',
  onValueChange,
  multiple = false,
  triggerClassName,
  renderItem,
  popoverContentClassName,
  emptyText = i18n.t('Common:noResultsFound'),
  searchPlaceholder,
  popoverModal,
  onSearch,
  loading,
}: DataTableFacetedFilterProps<TOption>) {
  const selected = React.useMemo(
    () => normaliseSelected(value, multiple),
    [value, multiple],
  );

  // Only search selectable options for the selected value display.
  const selectableOptions = React.useMemo(
    () =>
      options.filter(
        (o): o is TOption & { type?: 'option' } =>
          !('type' in o) || o.type === 'option' || o.type === undefined,
      ),
    [options],
  );

  const groups = React.useMemo(() => buildOptionGroups(options), [options]);

  // Remember labels for values we have seen so the trigger badge keeps showing
  // the selected option's name even after async `options` change (e.g. the user
  // searches again and the previously-selected option drops out of the list).
  const [labelCache, setLabelCache] = React.useState<Map<string, string>>(
    () => new Map(),
  );
  React.useEffect(() => {
    setLabelCache((prev) => {
      let next: Map<string, string> | null = null;
      for (const o of selectableOptions) {
        if (prev.get(o.value) !== o.getLabel()) {
          next ??= new Map(prev);
          next.set(o.value, o.getLabel());
        }
      }
      return next ?? prev;
    });
  }, [selectableOptions]);

  const labelFor = React.useCallback(
    (val: string) =>
      selectableOptions.find((o) => o.value === val)?.getLabel() ??
      labelCache.get(val) ??
      null,
    [selectableOptions, labelCache],
  );

  // --- Trigger label / badge ---
  const triggerBadge = React.useMemo(() => {
    if (!hasSelection(selected)) return null;

    if (multiple) {
      // Multi-select: show count, or single label if only one selected
      if (selected.size === 1) {
        return labelFor(Array.from(selected)[0]);
      }
      return i18n.t('Common:countSelected', { count: selected.size });
    }

    // Single-select: show the selected option's label
    return labelFor(Array.from(selected)[0]);
  }, [selected, multiple, labelFor]);

  // --- Selection handlers ---
  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (multiple) {
        const next = new Set(selected);
        if (next.has(optionValue)) {
          next.delete(optionValue);
        } else {
          next.add(optionValue);
        }
        const emit = onValueChange as
          | ((v: string | string[]) => void)
          | undefined;
        emit?.(next.size > 0 ? Array.from(next) : '__all__');
      } else {
        // Single-select toggle
        const emit = onValueChange as ((v: string) => void) | undefined;
        emit?.(selected.has(optionValue) ? '__all__' : optionValue);
      }
    },
    [multiple, selected, onValueChange],
  );

  const handleClear = React.useCallback(() => {
    (onValueChange as ((v: string) => void) | undefined)?.('__all__');
  }, [onValueChange]);

  return (
    <Popover modal={popoverModal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('tw:border-dashed', triggerClassName)}
        >
          {!hasSelection(selected) && <PlusCircle />}
          {title}
          {triggerBadge && (
            <>
              <Separator orientation="vertical" className="tw:mx-2 tw:h-full" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal tw:max-w-[150px]"
              >
                <span className="tw:truncate">{triggerBadge}</span>
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('tw:w-[200px] tw:p-0', popoverContentClassName)}
        align="start"
      >
        <Command shouldFilter={onSearch ? false : undefined}>
          <CommandInput
            placeholder={searchPlaceholder ?? title}
            onValueChange={onSearch}
          />
          <CommandList>
            {loading && (
              <div className="tw:flex tw:items-center tw:justify-center tw:py-6">
                <Spinner />
              </div>
            )}
            {!loading && <CommandEmpty>{emptyText}</CommandEmpty>}
            {!loading &&
              groups.map((group, groupIdx) => (
                <React.Fragment key={group.heading ?? `group-${groupIdx}`}>
                  {group.showSeparator && <CommandSeparator />}
                  <CommandGroup heading={group.heading}>
                    {group.items.map((option) => {
                      const isSelected = selected.has(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          className="tw:group/command-item"
                          onSelect={() => handleSelect(option.value)}
                        >
                          <div
                            className={cn(
                              'tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                              isSelected
                                ? 'tw:bg-primary tw:border-primary'
                                : 'tw:border-input tw:[&_svg]:invisible',
                            )}
                          >
                            <Check
                              className={cn(
                                'tw:size-3.5',
                                isSelected &&
                                  'tw:text-white! tw:dark:text-black!',
                              )}
                            />
                          </div>
                          {renderItem ? (
                            renderItem(option, isSelected)
                          ) : (
                            <>
                              {'icon' in option &&
                                option.icon &&
                                React.createElement(
                                  option.icon as React.ComponentType<{
                                    className?: string;
                                  }>,
                                  {
                                    className:
                                      'tw:text-muted-foreground tw:size-4',
                                  },
                                )}
                              <span>{option.getLabel()}</span>
                            </>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </React.Fragment>
              ))}
          </CommandList>
          {hasSelection(selected) && (
            <>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={handleClear}
                  className="tw:justify-center tw:text-center"
                >
                  {i18n.t('Order:filterClear')}
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
